import React, { useEffect, useState } from "react";
import SelectCarrier from "../../SelectCarrier/SelectCarrier";
import { trackEvent } from "../../../lib/analytics";
import { updateAgent } from "../../../Store/apiStore";
import { buildForwardingPayload } from "../../../lib/callForwardingCodes";
import styles from "./CarrierSelectStep.module.css";

// CarrierSelectStep — wraps <SelectCarrier/> inside the wizard. Keeps the
// exact search + 8-carrier radio list UI. wizardMode makes Continue hand
// off to onNext (instead of routing to /forward-instructions) and makes
// the "Go to Dashboard" modal close the wizard (via onWizardClose).
//
// GoLive variant also surfaces the post-purchase confirmation toast:
// when GoLiveWizard's confirmAndPurchase() succeeds it stashes
// `goLiveWizard.numberAssignedToast=1` in sessionStorage. We read +
// clear it on mount, show the green chip for 2.5s, then auto-dismiss.

// Review switch — `false` rakho (asli behaviour): toast sirf number assign hone
// par aata hai aur 2.5s me khud band ho jaata hai.
// Toast ka UI dobara dekhna ho to thodi der ke liye `true` karo: tab carrier
// screen khulte hi toast dikhta hai aur band nahi hota. Commit se pehle wapas
// `false`.
const KEEP_ASSIGNED_TOAST_OPEN = false;

export default function CarrierSelectStep({
  onNext,
  onBack,
  onSkip,
  agentCtx,
  wizardSkipPublishDirectly = false,
  // GoLive only — Continue opens the "When Call Forwarding?" sheet, and the
  // picked conditions ride along to onNext(carrier, conditions).
  conditionPrompt = false,
}) {
  // Resolve agent display name for SelectCarrier's "I will publish
  // {name}'s number directly" footer — only used when GoLiveWizard
  // turns on the publish-directly skip pattern.
  const resolvedAgentName =
    agentCtx?.agent?.agentName ||
    agentCtx?.agent?.name ||
    "Sophia";
  const [ready, setReady] = useState(false);
  const [showAssignedToast, setShowAssignedToast] = useState(false);

  useEffect(() => {
    if (agentCtx?.loading) return;
    if (agentCtx?.agentId)
      sessionStorage.setItem("SelectAgentId", agentCtx.agentId);
    if (agentCtx?.business?.businessId)
      sessionStorage.setItem(
        "SelectAgentBusinessId",
        agentCtx.business.businessId,
      );
    setReady(true);
  }, [agentCtx]);

  // Toast trigger — read once on mount. Cleared from sessionStorage
  // immediately so a manual refresh on the carrier screen doesn't
  // re-show it (the assignment already happened previously).
  useEffect(() => {
    if (!ready) return;
    if (KEEP_ASSIGNED_TOAST_OPEN) {
      // Review mode: hamesha dikhao, band mat karo. Nishaan phir bhi saaf kar
      // do taaki switch band karne ke baad purana nishaan toast na dikha de.
      try {
        sessionStorage.removeItem("goLiveWizard.numberAssignedToast");
      } catch { }
      setShowAssignedToast(true);
      return undefined;
    }
    let cue = null;
    try {
      cue = sessionStorage.getItem("goLiveWizard.numberAssignedToast");
      sessionStorage.removeItem("goLiveWizard.numberAssignedToast");
    } catch { }
    if (cue !== "1") return;
    setShowAssignedToast(true);
    const t = setTimeout(() => setShowAssignedToast(false), 2500);
    return () => clearTimeout(t);
  }, [ready]);

  if (!ready) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>
        Loading carriers…
      </div>
    );
  }

  // Agent's voip number — the number every dial code forwards TO. Same
  // parse as ForwardInstructions' Layer-1 lookup.
  const resolveVoipNumber = () => {
    const voip = agentCtx?.agent?.voip_numbers;
    if (!voip) return "";
    try {
      const parsed = typeof voip === "string" ? JSON.parse(voip) : voip;
      const first = Array.isArray(parsed) ? parsed[0] : parsed;
      return first || "";
    } catch {
      return "";
    }
  };

  // Sheet Continue — persist the selection + codes onto the agent row, then
  // advance. Awaited by SelectCarrier so the sheet shows "Saving…".
  const handleConditionsNext = async (carrier, conditions) => {
    const payload = buildForwardingPayload({
      carrier,
      number: resolveVoipNumber(),
      conditions,
    });

    trackEvent("call_forwarding_conditions_selected", {
      carrier: payload.carrier,
      conditions: payload.conditions.join(","),
      condition_count: payload.conditions.length,
      code_count: payload.codes.length,
      mode: payload.mode,
      network: payload.network,
    });

    if (agentCtx?.agentId) {
      try {
        await updateAgent(agentCtx.agentId, {
          forwardingCarrier: payload.carrier,
          forwardingConditions: {
            ...payload,
            dialedConditions: [],
            status: "selected",
            source: "go_live_wizard",
            updatedAt: new Date().toISOString(),
          },
        });
      } catch (e) {
        // Non-blocking: sessionStorage already holds the selection, so the
        // next screen renders correctly even if the write failed.
        console.error("Failed to persist forwardingConditions:", e);
      }
    }

    onNext(carrier, payload.conditions);
  };

  // Back / Skip live in the wizard shell footer; onSkip is still
  // forwarded to the component's "Go to Dashboard" modal as the abandon
  // escape hatch.
  return (
    <>
      {/* "Number assigned successfully!" toast — style CarrierSelectStep.module.css me. */}
      {showAssignedToast && (
        <div role="status" aria-live="polite" className={styles.assignedToast}>
          <span className={styles.assignedToastIcon} aria-hidden="true">
            <svg width="17" height="17" viewBox="0 0 16 16" fill="none">
              <path
                d="M3.5 8L6.5 11L12.5 5"
                stroke="#fff"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.assignedToastText}>Number assigned successfully!</span>
        </div>
      )}
      <SelectCarrier
        wizardMode={true}
        conditionPrompt={conditionPrompt}
        onConditionPromptShown={(carrier) => {
          const picked =
            typeof carrier === "string" ? carrier : carrier?.name || "";
          // In the Go-Live wizard, TAPPING a carrier is the selection: the
          // conditions sheet opens on select (see SelectCarrier). Record the
          // carrier first, then the prompt it triggered, so the journey reads
          // in the order the user acted. carrier_selected used to fire only
          // from onWizardNext below, after the whole sheet was completed, which
          // put it AFTER the prompt it caused.
          trackEvent("call_forwarding_carrier_selected", { carrier: picked });
          trackEvent("call_forwarding_condition_prompt_shown", { carrier: picked });
        }}
        onWizardNext={(carrier, conditions) => {
          // Only the legacy path (no conditions sheet) commits the carrier
          // here. With the sheet, it was recorded when the carrier was tapped.
          if (!conditionPrompt) {
            trackEvent("call_forwarding_carrier_selected", {
              carrier: typeof carrier === "string" ? carrier : carrier?.name || "",
            });
          }
          // conditionPrompt path hands us the sheet's selection — persist it
          // before advancing. Legacy path (no sheet) advances immediately.
          if (conditionPrompt && Array.isArray(conditions)) {
            return handleConditionsNext(carrier, conditions);
          }
          onNext(carrier);
          return undefined;
        }}
        onWizardBack={onBack}
        onWizardClose={onSkip}
        wizardSkipPublishDirectly={wizardSkipPublishDirectly}
        agentName={resolvedAgentName}
        onWizardSkipPublishDirectly={onSkip}
      />
    </>
  );
}
